import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Abi } from 'viem'
import { usePublicClient } from 'wagmi'

import { MULTICALL_ABI, MULTICALL_NETWORKS } from '../../constants/multicall'
import { useWidgetChainScope } from '../../config'
import { readBalancesUiDebug } from '../../utils/debugReadBalancesUi'
import useIsWindowVisible from '../../hooks/useIsWindowVisible'
import { AppDispatch, AppState } from '../index'
import { parseCallKey, toCallKey, updateMulticallResults } from './actions'
import { writeImmutableCallCache } from './immutableCallCache'
import { selectCallsFingerprint, selectMulticallPollIntervalMs } from './pollSchedule'

const multicallAbi = MULTICALL_ABI as Abi

const MULTICALL_AGGREGATE_ERROR_LOG_MIN_MS = 30_000

export default function MulticallUpdater() {
  const { chainId } = useWidgetChainScope()
  const publicClient = usePublicClient({ chainId })
  const dispatch = useDispatch<AppDispatch>()
  const lastAggregateErrorLogAtRef = useRef(0)
  const windowVisible = useIsWindowVisible()

  const multicallAddress = chainId != null ? MULTICALL_NETWORKS[chainId as keyof typeof MULTICALL_NETWORKS] : undefined

  const callsFingerprint = useSelector(
    (state: AppState) => selectCallsFingerprint(state, chainId),
    (a, b) => a === b,
  )

  const pollIntervalMs = useSelector((state: AppState) => selectMulticallPollIntervalMs(state, chainId))

  useEffect(() => {
    if (!publicClient || chainId == null || !multicallAddress || !windowVisible) return
    const callKeys: string[] = JSON.parse(callsFingerprint) as string[]
    if (callKeys.length === 0) return
    const calls = callKeys.map((key) => parseCallKey(key))

    const fetchAll = async () => {
      try {
        const aggregateArgs = calls.map((c) => ({
          target: c.address as `0x${string}`,
          callData: c.callData as `0x${string}`,
        }))

        const result = await publicClient.readContract({
          address: multicallAddress as `0x${string}`,
          abi: multicallAbi,
          functionName: 'aggregate',
          args: [aggregateArgs],
        })

        const [blockNum, returnData] = result as [bigint, readonly `0x${string}`[]]

        const results = calls.reduce<{ [callKey: string]: string | null }>((memo, call, i) => {
          memo[toCallKey(call)] = returnData[i] ?? null
          return memo
        }, {})

        dispatch(
          updateMulticallResults({
            chainId,
            blockNumber: Number(blockNum),
            results,
          }),
        )

        for (const call of calls) {
          const data = results[toCallKey(call)]
          if (data) {
            writeImmutableCallCache(chainId, call, data, Number(blockNum))
          }
        }
      } catch (err: unknown) {
        const now = Date.now()
        if (now - lastAggregateErrorLogAtRef.current >= MULTICALL_AGGREGATE_ERROR_LOG_MIN_MS) {
          lastAggregateErrorLogAtRef.current = now
          readBalancesUiDebug('MulticallUpdater.aggregateError', {
            chainId,
            callCount: calls.length,
            message: err instanceof Error ? err.message : String(err),
          })
        }
      }
    }

    void fetchAll()
    const interval = window.setInterval(() => void fetchAll(), pollIntervalMs)

    return () => window.clearInterval(interval)
  }, [publicClient, chainId, multicallAddress, callsFingerprint, dispatch, pollIntervalMs, windowVisible])

  return null
}
